import { useEffect, useState, ChangeEvent } from "react";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

const FAMILY_BASE_URL = `${API_BASE_PATH}/family`;

export interface FamilyformDataFamily {
  id?: number;
  employee_id?: string;
  name: string;
  relationship_type: string;
  dob: string;
  phone: string;
}

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// 🔹 Hook now accepts both employeeId and token
export const useFamilyInfo = (
  employeeId: string | null,
  token: string | null
) => {
  const [formDataFamily, setFormDataFamily] = useState<FamilyformDataFamily>({
    name: "",
    relationship_type: "",
    dob: "",
    phone: "",
  });

  const [familyList, setFamilyList] = useState<FamilyformDataFamily[]>([]);

  const getFamilyInfo = async () => {
    if (!employeeId) return;
    try {
      const res = await axios.get(
        `${FAMILY_BASE_URL}/employee/all/${employeeId}`,
        {
          headers: {
            accept: "application/json",
            authorization: "Bearer " + token,
          },
        }
      );

      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setFamilyList(list);
    } catch (err: any) {
      const message = extractErrorMessage(err);
      console.error("❌ Error fetching family info", message);
      toast.error(message);
      setFamilyList([]); // ✅ Fallback to empty array on error
    }
  };

  useEffect(() => {
    getFamilyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, token]); // 🔹 Refetch if token changes

  const handleChangeFamily = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormDataFamily((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChangeFamily = (date: Dayjs | null) => {
    if (date) {
      setFormDataFamily((prev) => ({
        ...prev,
        dob: date.format("YYYY-MM-DD"),
      }));
    }
  };

  const clearForm = () => {
    setFormDataFamily({
      name: "",
      relationship_type: "",
      dob: "",
      phone: "",
    });
  };

  const handleSubmitFamily = async () => {
    if (!employeeId) {
      toast.error("Employee ID is missing");
      return;
    }

    const { name, relationship_type, dob } = formDataFamily;

    if (!name || !relationship_type || !dob) {
      toast.warn("Please fill in Name Relationship and Date of Birth");
      return;
    }

    try {
      const payload = {
        employee_id: employeeId,
        name: formDataFamily.name,
        relationship_type: formDataFamily.relationship_type,
        dob: formDataFamily.dob,
        phone: formDataFamily.phone,
      };

      if (formDataFamily.id) {
        await axios.put(`${FAMILY_BASE_URL}/${formDataFamily.id}`, payload, {
          headers: {
            accept: "application/json",
            authorization: "Bearer " + token,
          },
        });
        toast.success("Family information updated");
      } else {
        await axios.post(`${FAMILY_BASE_URL}/`, payload, {
          headers: {
            accept: "application/json",
            authorization: "Bearer " + token,
          },
        });
        toast.success("Family information added");
      }

      clearForm();
      await getFamilyInfo();
    } catch (err: any) {
      const message = extractErrorMessage(err);
      console.error("❌ Error submitting family info:", message);
      toast.error(message);
    }
  };

  const handleEditFamily = (record: FamilyformDataFamily) => {
    setFormDataFamily({ ...record });
  };

  const handleNewFamilyClick = () => {
    clearForm();
  };

  return {
    formDataFamily,
    familyList,
    handleChangeFamily,
    handleDateChangeFamily,
    handleSubmitFamily,
    handleEditFamily,
    handleNewFamilyClick,
    getFamilyInfo,
    clearForm,
  };
};
